// Kasus Pengembalian Shopee: status Shopee (dari Laporan) terpisah dari pemeriksaan fisik Gudang & keputusan Owner.
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "sonner";
import { X, Route } from "lucide-react";
import { ShopeeJourneyModal, fmtAt } from "./order_track";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/shopee-shipping-report/return-cases`;
const opt = { withCredentials: true };
const FIELD = { final_cause: "Jenis kesalahan", solution: "Solusi", evidence_note: "Catatan bukti", change_reason: "Alasan perubahan",
  condition: "Kondisi barang", note: "Catatan masalah", responsible: "Penanggung jawab" };
// detail 422 FastAPI berupa daftar objek → ubah jadi teks (merender objek membuat halaman blank).
const errOf = (e, d) => {
  const x = e?.response?.data?.detail;
  if (Array.isArray(x)) return x.map(v => {
    const f = FIELD[(v?.loc || []).slice(-1)[0]] || "Isian";
    return v?.type === "string_too_short" ? `${f} wajib diisi (minimal ${v?.ctx?.min_length ?? 1} karakter)`
      : v?.type === "string_too_long" ? `${f} terlalu panjang` : `${f} tidak valid`;
  }).join(". ");
  return typeof x === "string" && x ? x : d;
};

const histVal = (o) => Object.entries(o || {}).filter(([, v]) => v && (!Array.isArray(v) || v.length)).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`).join("; ") || "—";

const DSTATUS = { belum: "Belum Ditetapkan", sementara: "Sementara", ditetapkan: "Ditetapkan" };
const respText = (c) => (c?.responsible || []).join(", ") || c?.responsible_stage || "—";

export function OpenCases({ onOpen, reloadKey }) {
  const [d, setD] = useState(null), [show, setShow] = useState(false);
  useEffect(() => { axios.get(`${API}/open`, opt).then(r => setD(r.data)).catch(() => setD({ rows: [], total: 0, error: true })); }, [reloadKey]);
  if (!d) return null;
  return <div className="ssr-other" data-testid="return-open-cases">
    <button type="button" onClick={() => setShow(!show)} aria-expanded={show} data-testid="return-open-toggle">
      <span>Pengembalian Berjalan & Belum Ditetapkan (semua minggu)</span><b data-testid="return-open-total">{d.error ? "?" : d.total}</b><i>{show ? "▲" : "▼"}</i></button>
    {show && <ul className="ssr-list">{d.rows.length ? d.rows.map(r => <li key={r.return_sn} className="ssr-row" data-testid={`return-open-${r.return_sn}`}>
      <div className="ssr-row-head"><b>{r.order_sn}</b><span>{r.status_label}</span></div>
      <div className="ssr-row-kind">Masalah: {r.reason || "—"}</div>
      <div className="ssr-row-kind" data-testid={`return-open-resp-${r.return_sn}`}>Bagian / penanggung jawab: {r.case?.responsible_stage ? `${r.case.responsible_stage} · ${respText(r.case)}` : "—"}</div>
      <CaseBadge sn={r.return_sn} s={r.case} onOpen={() => onOpen(r.return_sn)}/></li>) : <li className="ssr-empty">Tidak ada kasus berjalan.</li>}</ul>}
  </div>;
}

export function useCaseSummaries(sns) {
  const [m, setM] = useState({});
  const key = sns.join(",");
  const load = useCallback(() => {
    if (!key) return;
    axios.get(API, { ...opt, params: { sns: key } }).then(r => setM(r.data)).catch(() => {});
  }, [key]);
  useEffect(() => { load(); }, [load]);
  return [m, load];
}

export function CaseBadge({ s, onOpen, sn }) {
  const ds = s?.decision_status || (s?.decided ? "ditetapkan" : "belum");
  const label = !s ? "Kasus" : ds !== "belum" ? DSTATUS[ds] : s.inspected ? "Diperiksa · Belum Ditetapkan" : "Belum Diperiksa";
  return <button type="button" className={`ssr-case-btn${ds === "ditetapkan" ? " is-done" : ds === "sementara" || s?.inspected ? " is-mid" : ""}`} onClick={onOpen}
    data-testid={`return-case-open-${sn}`}>{label} ›</button>;
}

function Field({ label, children }) {
  return <label className="rc-field"><span>{label}</span>{children}</label>;
}

function Inspection({ sn, d, onSaved, onPhoto }) {
  const i = d.case.inspection || {};
  const [f, setF] = useState({ condition: i.condition || "", note: i.note || "", suspected_stage: i.suspected_stage || "",
    suspected_note: i.suspected_note || "", solution_status: i.solution_status || "belum_diketahui", solution_note: i.solution_note || "" });
  const [busy, setBusy] = useState(false), [msg, setMsg] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    setBusy(true); setMsg("");
    try { await axios.put(`${API}/${sn}/inspection`, f, opt); setMsg("Tersimpan"); onSaved(); }
    catch (e) { setMsg(errOf(e, "Gagal menyimpan")); } finally { setBusy(false); }
  };
  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file); setBusy(true); setMsg("");
    try { await axios.post(`${API}/${sn}/photos`, fd, opt); onSaved(); } catch (er) { setMsg(errOf(er, "Gagal unggah foto")); }
    finally { setBusy(false); e.target.value = ""; }
  };
  return <section className="rc-sec" data-testid="return-case-inspection">
    <h4>Pemeriksaan Barang Fisik (Gudang)</h4>
    {i.at && <p className="otr-meta">Terakhir oleh {i.by} · {fmtAt(i.at)}</p>}
    <Field label="Kondisi barang"><input value={f.condition} onChange={set("condition")} data-testid="rc-condition"/></Field>
    <Field label="Catatan masalah"><textarea rows={2} value={f.note} onChange={set("note")} data-testid="rc-note"/></Field>
    <Field label="Dugaan penyebab (tahap)"><select value={f.suspected_stage} onChange={set("suspected_stage")} data-testid="rc-suspected-stage">
      <option value="">— belum dipilih —</option>{d.stages.map(s => <option key={s}>{s}</option>)}</select></Field>
    <Field label="Keterangan dugaan (bukti)"><textarea rows={2} value={f.suspected_note} onChange={set("suspected_note")} data-testid="rc-suspected-note"/></Field>
    <div className="rc-radios" data-testid="rc-solution">{Object.entries(d.solutions).map(([k, v]) =>
      <label key={k}><input type="radio" name={`sol-${sn}`} checked={f.solution_status === k} onChange={() => setF({ ...f, solution_status: k })} data-testid={`rc-solution-${k}`}/> {v}</label>)}</div>
    <Field label="Catatan solusi"><input value={f.solution_note} onChange={set("solution_note")} data-testid="rc-solution-note"/></Field>
    <div className="rc-photos">{(d.case.photos || []).map(p => <button type="button" key={p.id} className="rc-thumb" onClick={() => onPhoto(p.id)} aria-label="Buka foto bukti">
      <img src={`${API}/photos/${p.id}`} alt="Bukti" data-testid={`rc-photo-${p.id}`}/></button>)}
      <label className="soft-btn small-btn rc-upload">+ Foto bukti<input type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={upload} data-testid="rc-photo-input"/></label></div>
    <div className="rc-actions"><button type="button" className="primary" disabled={busy} onClick={save} data-testid="rc-inspection-save">Simpan Pemeriksaan</button>
      {msg && <span className="otr-meta" data-testid="rc-inspection-msg">{msg}</span>}</div>
  </section>;
}

const NO_BLAME = ["Bukan Kesalahan Internal", "Pembeli", "Pengiriman/Ekspedisi", "Belum diketahui"];

// Bukti dari data yang sudah tersimpan (pemeriksaan Gudang / foto). Kosong → perlu "Bukti singkat".
export const storedEvidence = (c) => [c?.inspection?.note, (c?.photos || []).length ? `${c.photos.length} foto bukti tersimpan` : ""].filter(Boolean).join(" · ");

function StagePick({ stages, value, onPick }) {
  return <div className="rc-radios" role="radiogroup" aria-label="Kesalahan bagian mana">
    {stages.map(s => <label key={s} className={`rc-radio${value === s ? " is-on" : ""}`} data-testid={`rc-stage-${s.replace(/\W+/g, "-")}`}>
      <input type="radio" name="rc-stage" checked={value === s} onChange={() => onPick(s)}/>{s}</label>)}
  </div>;
}

function Decision({ sn, d, onSaved, onFinal, onRetry }) {
  const x = d.case.decision || {};
  const [f, setF] = useState({ final_cause: x.final_cause || "", responsible_stage: x.responsible_stage || "", responsible: x.responsible || [], change_reason: "", proof: "" });
  const [busy, setBusy] = useState(false), [msg, setMsg] = useState(""), [ok, setOk] = useState(false);
  const busyRef = useRef(false);
  const noBlame = NO_BLAME.includes(f.responsible_stage);
  const names = d.manual_allowed ? (d.employees || []).map(e => e.name) : (d.handlers || {})[f.responsible_stage] || [];
  const teamOf = Object.fromEntries((d.employees || []).map(e => [e.name, e.team]));
  const stored = storedEvidence(d.case);
  const isFinal = d.case.decision ? x.final !== false : false;
  const status = DSTATUS[!d.case.decision ? "belum" : isFinal ? "ditetapkan" : "sementara"];
  const toggle = (n) => setF({ ...f, responsible: f.responsible.includes(n) ? f.responsible.filter(v => v !== n) : [...f.responsible, n] });
  if (!d.can_decide) return <section className="rc-sec" data-testid="return-case-decision">
    <h4>Penetapan Kesalahan · <span data-testid="rc-decision-status">{status}</span></h4>
    {x.at ? <p>{x.final_cause}<br/><span className="otr-meta">{x.responsible_stage || "—"} · {(x.responsible || []).join(", ") || "—"} · {x.by} · {fmtAt(x.at)}</span></p>
      : <p className="otr-meta">Belum ada penetapan.</p>}
  </section>;
  const save = async (final) => {
    if (busyRef.current) return;  // cegah simpan ganda
    if (!f.responsible_stage) { setOk(false); setMsg("Pilih bagian yang bertanggung jawab."); return; }
    if (!noBlame && !f.responsible.length) { setOk(false); setMsg("Pilih minimal satu karyawan."); return; }
    if (!stored && !f.proof.trim()) { setOk(false); setMsg("Isi bukti singkat terlebih dahulu."); return; }
    busyRef.current = true; setBusy(true); setMsg("");
    try {
      if (!d.case.inspection) await axios.put(`${API}/${sn}/inspection`, { condition: "Diperiksa", note: f.proof.trim() || f.final_cause, solution_status: "belum_diketahui" }, opt);
      await axios.put(`${API}/${sn}/decision`, { final_cause: f.final_cause, solution: x.solution || "", responsible_stage: f.responsible_stage,
        responsible: noBlame ? [] : f.responsible, evidence_note: stored || f.proof.trim(), change_reason: f.change_reason, final,
        manual: !!d.manual_allowed && !noBlame }, opt);
      setOk(true); setMsg(final ? "Keputusan berhasil disimpan" : "Keputusan berhasil disimpan sebagai Sementara");
      if (final) onFinal(); else onSaved();
    } catch (e) { setOk(false); setMsg(errOf(e, "Gagal menyimpan keputusan. Coba lagi.")); } finally { busyRef.current = false; setBusy(false); }
  };
  return <section className="rc-sec rc-decide" data-testid="return-case-decision">
    <h4>Penetapan Kesalahan <span className={`rc-dstatus is-${!d.case.decision ? "belum" : isFinal ? "final" : "draft"}`} data-testid="rc-decision-status">{status}</span></h4>
    <div className="rc-q">1. Kesalahan bagian mana?</div>
    <StagePick stages={d.stages || []} value={f.responsible_stage} onPick={(s) => setF({ ...f, responsible_stage: s, responsible: [] })}/>
    {f.responsible_stage && !noBlame && <>
      <div className="rc-q">2. Siapa yang bertanggung jawab?</div>
      {d.handlers_error ? <div className="alert" data-testid="rc-handlers-error">{d.handlers_error}.{" "}
        <button type="button" className="soft-btn small-btn" onClick={onRetry} data-testid="rc-handlers-retry">Coba Lagi</button></div>
      : <>
      {d.manual_allowed && <p className="otr-meta" data-testid="rc-manual-note">Pesanan ini belum tercatat di alur produksi TAMFARIS. Pilih karyawan secara manual dari akun karyawan; penetapan dicatat sebagai sumber Manual.</p>}
      {names.length ? <div className="rc-names">{names.map(n => <label key={n} className={`rc-radio${f.responsible.includes(n) ? " is-on" : ""}`}>
        <input type="checkbox" checked={f.responsible.includes(n)} onChange={() => toggle(n)} data-testid={`rc-resp-${n}`}/>{n}
        {d.manual_allowed && teamOf[n] && <span className="otr-meta"> · {teamOf[n]}</span>}</label>)}</div>
        : <p className="otr-meta" data-testid="rc-no-handlers">Tidak ada petugas tercatat pada bagian ini.</p>}
      {!d.manual_allowed && <p className="otr-meta">Pilihan nama mengikuti riwayat petugas pesanan.</p>}</>}</>}
    {noBlame && <p className="otr-meta" data-testid="rc-noblame-note">Tidak dicatat sebagai kesalahan karyawan.</p>}
    <div className="rc-q">{noBlame ? "2" : "3"}. Apa kesalahannya?</div>
    <textarea rows={2} value={f.final_cause} onChange={e => setF({ ...f, final_cause: e.target.value })} placeholder="Contoh: Tulisan bordir belakang tidak sesuai desain yang disetujui."
      data-testid="rc-final-cause"/>
    {!stored && <Field label="Bukti singkat (belum ada pemeriksaan/foto tersimpan)"><input value={f.proof} onChange={e => setF({ ...f, proof: e.target.value })} data-testid="rc-proof"/></Field>}
    {isFinal && <Field label="Alasan koreksi (wajib bila mengubah keputusan yang sudah Ditetapkan)"><input value={f.change_reason} onChange={e => setF({ ...f, change_reason: e.target.value })} data-testid="rc-change-reason"/></Field>}
    <div className="rc-actions">
      {!isFinal && <button type="button" className="soft-btn" disabled={busy} onClick={() => save(false)} data-testid="rc-decision-save-draft">Simpan Sementara</button>}
      <button type="button" className="primary" disabled={busy} onClick={() => save(true)} data-testid="rc-decision-save">{isFinal ? "Simpan Koreksi" : "Tetapkan Kesalahan"}</button>
    </div>
    {msg && <p className={ok ? "rc-ok" : "alert"} role="status" data-testid="rc-decision-msg">{msg}</p>}
    <p className="otr-meta">Sementara belum masuk Riwayat Kesalahan karyawan.</p>
  </section>;
}

export function ReturnCaseModal({ sn, onClose, onChanged }) {
  const [d, setD] = useState(null), [err, setErr] = useState(""), [journey, setJourney] = useState(false);
  const load = useCallback(() => axios.get(`${API}/${sn}`, opt).then(r => setD(r.data)).catch(e => setErr(errOf(e, "Gagal memuat kasus"))), [sn]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const p = document.body.style.overflow; document.body.style.overflow = "hidden"; return () => { document.body.style.overflow = p; }; }, []);
  const saved = () => { load(); onChanged?.(); };
  const [photo, setPhoto] = useState(null);
  // Tombol Kembali perangkat menutup foto (bukan keluar dari kasus).
  const openPhoto = (pid) => { window.history.pushState({ rcPhoto: pid }, ""); setPhoto(pid); };
  const closePhoto = () => { if (window.history.state?.rcPhoto) window.history.back(); else setPhoto(null); };
  useEffect(() => { const pop = () => setPhoto(null); window.addEventListener("popstate", pop); return () => window.removeEventListener("popstate", pop); }, []);
  const s = d?.shopee;
  return createPortal(<div className="modal-overlay modal-top" onClick={onClose} data-testid="return-case-overlay">
    <div className="modal-content" onClick={e => e.stopPropagation()}>
      <div className="modal-header"><h3>Pengembalian Shopee</h3>
        <button type="button" onClick={onClose} aria-label="Tutup" data-testid="return-case-close"><X size={18}/></button></div>
      <div className="modal-body">
        {err && <div className="otr-notice" role="alert">{err}</div>}
        {!d && !err && <p className="otr-meta">Memuat…</p>}
        {d && <>
          <section className="rc-sec" data-testid="return-case-shopee">
            <p>Pesanan <b>{s.order_sn}</b> · <b data-testid="rc-shopee-status">{s.status_label}</b></p>
            <div className="rc-problem"><b>Masalah</b><div>{[s.reason, s.text_reason].filter(Boolean).join(" — ") || "—"}</div></div>
            {(d.case.photos || []).length > 0 && <div className="rc-photos">{d.case.photos.map(p => <button type="button" key={p.id} className="rc-thumb" onClick={() => openPhoto(p.id)} aria-label="Buka foto bukti"><img src={`${API}/photos/${p.id}`} alt="Bukti" data-testid={`rc-top-photo-${p.id}`}/></button>)}</div>}
          </section>
          <Decision sn={sn} d={d} onSaved={saved} onRetry={load} onFinal={() => { onChanged?.(); toast.success("Keputusan berhasil disimpan"); onClose(); }}/>
          <details className="rc-more" data-testid="rc-more">
            <summary>Informasi tambahan: pemeriksaan Gudang, riwayat pesanan & riwayat kasus</summary>
            <p className="otr-meta">Diajukan {fmtAt(s.create_time_iso) || "—"} · diperbarui Shopee {fmtAt(s.update_time_iso) || "—"} · retur {sn}</p>
            <button type="button" className="soft-btn small-btn" onClick={() => setJourney(true)} data-testid="rc-journey-btn"><Route size={13}/> Riwayat Perjalanan Pesanan</button>
            <Inspection sn={sn} d={d} onSaved={saved} onPhoto={openPhoto}/>
            {(d.case.history || []).length > 0 && <section className="rc-sec" data-testid="return-case-history"><h4>Riwayat Kasus</h4>
              <ol className="rc-hist">{[...d.case.history].reverse().map((h, i) => <li key={i}><b>{h.action}</b>{h.detail ? ` · ${h.detail}` : ""}<div className="otr-meta">{h.by} · {fmtAt(h.at)}{h.reason ? ` · Alasan: ${h.reason}` : ""}</div>
                {h.before && <div className="otr-meta">Sebelum: {histVal(h.before)} → Sesudah: {histVal(h.after)}</div>}</li>)}</ol></section>}
          </details>
        </>}
      </div>
    </div>
    {journey && s && <ShopeeJourneyModal orderNumber={s.order_sn} onClose={() => setJourney(false)}/>}
    {photo && <PhotoView id={photo} onClose={closePhoto}/>}
  </div>, document.body);
}

// Foto bukti layar penuh: X selalu terlihat (aman dari status bar), tombol Kembali perangkat menutup, cubit/geser untuk memperbesar.
function PhotoView({ id, onClose }) {
  const [zoom, setZoom] = useState(false);
  useEffect(() => { const k = (e) => e.key === "Escape" && onClose(); window.addEventListener("keydown", k); return () => window.removeEventListener("keydown", k); }, [onClose]);
  return createPortal(<div className="rc-pv" role="dialog" aria-modal="true" aria-label="Foto bukti" data-testid="rc-photo-view"
    onClick={(e) => { e.stopPropagation(); if (e.target === e.currentTarget) onClose(); }}>
    <button type="button" className="rc-pv-close" onClick={onClose} aria-label="Tutup foto" data-testid="rc-photo-close"><X size={22}/> Tutup</button>
    <div className={`rc-pv-scroll${zoom ? " is-zoom" : ""}`} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <img src={`${API}/photos/${id}`} alt="Foto bukti" onClick={() => setZoom(z => !z)} data-testid="rc-photo-full"/>
    </div>
  </div>, document.body);
}
